import { useParams } from 'react-router-dom';
import type {
    DiaryDocType,
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    OverallDescriptionDocType,
    AreaDescriptionDocType,
    SampleDocType
} from '@ast-data/shared';
import FindingFormDialog from '../findings/FindingFormDialog';
import FindingPlanumFormDialog from '../findings/FindingPlanumFormDialog';
import FindingProfileFormDialog from '../findings/FindingProfileFormDialog';
import DiaryFormDialog from '../findings/DiaryFormDialog';
import OverallDescriptionFormDialog from '../findings/OverallDescriptionFormDialog';
import AreaDescriptionFormDialog from '../findings/AreaDescriptionFormDialog';
import SampleFormDialog from '../findings/SampleFormDialog';
import {
    useDialogClose,
    useDialogKey,
    useRecordSnapshot,
    useSeedRecord,
    useSeedWaiting
} from '../findings/recordDialogHooks';
import FormDialogShell from '../../components/FormDialogShell';

/**
 * The Befund / Planum / Profil editors reuse the OFFICE fullscreen dialogs in the
 * field too, mounted as child routes over the Befunde list (rendered via its
 * <Outlet />). Same components, same URL shape as office — only the close target
 * differs (back to the field list, not the office grid).
 */

interface DialogRouteProps {
    mode: 'new' | 'edit';
}

export function FieldFindingDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', findingId = '' } = useParams();
    const dialogKey = useDialogKey(mode, findingId);
    const close = useDialogClose(`/field/${projectId}/findings`);
    const finding = useRecordSnapshot<FindingDocType>(
        'findings',
        mode === 'edit' ? findingId : ''
    );
    if (mode === 'edit' && !finding) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="finding.form.editTitle"
            />
        );
    }
    return (
        <FindingFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            finding={finding}
        />
    );
}

export function FieldPlanumDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', findingId = '', planumId = '' } = useParams();
    const dialogKey = useDialogKey(mode, planumId);
    const close = useDialogClose(`/field/${projectId}/findings`);
    const planum = useRecordSnapshot<FindingPlanumDocType>(
        'finding_planums',
        mode === 'edit' ? planumId : ''
    );
    const seedFrom = useSeedRecord<FindingPlanumDocType>(
        'finding_planums',
        mode
    );
    const seedWaiting = useSeedWaiting(mode, seedFrom);
    if ((mode === 'edit' && !planum) || seedWaiting) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="planum.form.editTitle"
            />
        );
    }
    return (
        <FindingPlanumFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            // The doc is authoritative; the URL segment covers the new case.
            findingId={planum?.findingId || seedFrom?.findingId || findingId}
            planum={planum}
            seedFrom={seedFrom}
        />
    );
}

export function FieldProfileDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', findingId = '', profileId = '' } = useParams();
    const dialogKey = useDialogKey(mode, profileId);
    const close = useDialogClose(`/field/${projectId}/findings`);
    const profile = useRecordSnapshot<FindingProfileDocType>(
        'finding_profiles',
        mode === 'edit' ? profileId : ''
    );
    const seedFrom = useSeedRecord<FindingProfileDocType>(
        'finding_profiles',
        mode
    );
    const seedWaiting = useSeedWaiting(mode, seedFrom);
    if ((mode === 'edit' && !profile) || seedWaiting) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="profile.form.editTitle"
            />
        );
    }
    return (
        <FindingProfileFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            findingId={profile?.findingId || seedFrom?.findingId || findingId}
            profile={profile}
            seedFrom={seedFrom}
        />
    );
}

export function FieldDiaryDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', diaryId = '' } = useParams();
    const dialogKey = useDialogKey(mode, diaryId);
    const close = useDialogClose(`/field/${projectId}/diaries`);
    const diary = useRecordSnapshot<DiaryDocType>(
        'diaries',
        mode === 'edit' ? diaryId : ''
    );
    if (mode === 'edit' && !diary) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="diary.form.editTitle"
            />
        );
    }
    return (
        <DiaryFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            diary={diary}
            // Takes the TARGET's project: a Letzte-Nr. may name a Befund in a
            // sibling measure of the same family.
            findingsBasePath={(targetProjectId) =>
                `/field/${targetProjectId}/findings`
            }
            // The field Zeiterfassung is already one project's, and shows one
            // day at a time — so only the day has to be said.
            timeEntriesPath={(targetProjectId, date) =>
                `/field/${targetProjectId}/time-entries?date=${date}`
            }
        />
    );
}

export function FieldOverallDescriptionDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', overallDescriptionId = '' } = useParams();
    const dialogKey = useDialogKey(mode, overallDescriptionId);
    const close = useDialogClose(`/field/${projectId}/overall-descriptions`);
    const overallDescription = useRecordSnapshot<OverallDescriptionDocType>(
        'overall_descriptions',
        mode === 'edit' ? overallDescriptionId : ''
    );
    if (mode === 'edit' && !overallDescription) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="overallDescription.form.editTitle"
            />
        );
    }
    return (
        <OverallDescriptionFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            overallDescription={overallDescription}
        />
    );
}

export function FieldAreaDescriptionDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', areaDescriptionId = '' } = useParams();
    const dialogKey = useDialogKey(mode, areaDescriptionId);
    const close = useDialogClose(`/field/${projectId}/area-descriptions`);
    const areaDescription = useRecordSnapshot<AreaDescriptionDocType>(
        'area_descriptions',
        mode === 'edit' ? areaDescriptionId : ''
    );
    if (mode === 'edit' && !areaDescription) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="areaDescription.form.editTitle"
            />
        );
    }
    return (
        <AreaDescriptionFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            areaDescription={areaDescription}
        />
    );
}

export function FieldSampleDialogRoute({ mode }: DialogRouteProps) {
    const { projectId = '', sampleId = '' } = useParams();
    const dialogKey = useDialogKey(mode, sampleId);
    const close = useDialogClose(`/field/${projectId}/samples`);
    const sample = useRecordSnapshot<SampleDocType>(
        'samples',
        mode === 'edit' ? sampleId : ''
    );
    if (mode === 'edit' && !sample) {
        return (
            <FormDialogShell
                projectId={projectId}
                onClose={close}
                titleId="sample.form.editTitle"
            />
        );
    }
    return (
        <SampleFormDialog
            key={dialogKey}
            open
            onClose={close}
            projectId={projectId}
            sample={sample}
        />
    );
}
